import { createSlice } from '@reduxjs/toolkit'
import apiService from '../../services/api.service'
import { createApiThunk } from 'helper/create.api.thunk'

/**
 * The money side of the back office: withdrawals out, referral rewards in.
 *
 * One slice rather than two because the two screens answer the same question from
 * either end - what the business owes people, and what it has already paid them - and
 * they are read by the same two roles.
 */
export const getWithdrawalList = createApiThunk(
    'getWithdrawalList',
    apiService.getWithdrawalList
)

export const getWithdrawalInfo = createApiThunk(
    'getWithdrawalInfo',
    apiService.getWithdrawalInfo
)

export const approveWithdrawal = createApiThunk(
    'approveWithdrawal',
    apiService.approveWithdrawal
)

export const rejectWithdrawal = createApiThunk(
    'rejectWithdrawal',
    apiService.rejectWithdrawal
)

export const getReferralEarnings = createApiThunk(
    'getReferralEarnings',
    apiService.getReferralEarnings
)

export const initialData = {
    withdrawalSort: {
        active_page: 1,
        start: 0,
        length: 10,
        search: '',
        // The queue opens on what is waiting for a decision, because that is the only
        // reason anybody opens it.
        status: 'pending',
        sort: '',
        order: '',
        decision_loading: false,
    },
    withdrawalData: {
        list: [],
        total: 0,
        loading: false,
        // How many sit at each stage, for the summary cards above the table. They come
        // back with the listing rather than from a call of their own.
        tab_counts: {},
    },
    withdrawalInfo: null,
    withdrawalInfoLoading: false,

    earningSort: {
        active_page: 1,
        start: 0,
        length: 10,
        search: '',
        sort: '',
        order: '',
    },
    earningData: {
        list: [],
        total: 0,
        loading: false,
        // What the business has paid out in referral rewards, all time.
        total_rewarded: 0,
        summary: {},
    },
}

const dataSlice = createSlice({
    name: 'finance',
    initialState: initialData,
    reducers: {
        setWithdrawalSort: (state, action) => {
            state.withdrawalSort = action.payload
        },
        setEarningSort: (state, action) => {
            state.earningSort = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getWithdrawalList.pending, (state) => {
                state.withdrawalData.loading = true
            })
            .addCase(getWithdrawalList.fulfilled, (state, action) => {
                state.withdrawalData.loading = false
                state.withdrawalData.list = action.payload?.data?.aaData ?? []
                state.withdrawalData.total =
                    action.payload?.data?.iTotalDisplayRecords ?? 0
                state.withdrawalData.tab_counts =
                    action.payload?.data?.tab_counts ?? {}
            })
            .addCase(getWithdrawalList.rejected, (state) => {
                state.withdrawalData.loading = false
            })

            .addCase(getWithdrawalInfo.pending, (state) => {
                state.withdrawalInfoLoading = true
            })
            .addCase(getWithdrawalInfo.fulfilled, (state, action) => {
                state.withdrawalInfoLoading = false
                state.withdrawalInfo = action.payload?.data ?? null
            })
            .addCase(getWithdrawalInfo.rejected, (state) => {
                state.withdrawalInfoLoading = false
            })

            .addCase(getReferralEarnings.pending, (state) => {
                state.earningData.loading = true
            })
            .addCase(getReferralEarnings.fulfilled, (state, action) => {
                state.earningData.loading = false
                state.earningData.list = action.payload?.data?.aaData ?? []
                state.earningData.total =
                    action.payload?.data?.iTotalDisplayRecords ?? 0
                state.earningData.total_rewarded =
                    action.payload?.data?.total_rewarded ?? 0
                // The two figures the page leads with, straight off the listing.
                state.earningData.summary = action.payload?.data?.summary ?? {}
            })
            .addCase(getReferralEarnings.rejected, (state) => {
                state.earningData.loading = false
            })

        // A decision re-reads the row it changed, and flips `decision_loading` so the
        // listing reloads - otherwise the queue still shows a request that has just
        // been paid.
        ;[approveWithdrawal, rejectWithdrawal].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (state) => {
                    state.withdrawalSort.decision_loading = true
                })
                .addCase(thunk.fulfilled, (state, action) => {
                    state.withdrawalSort.decision_loading = false
                    state.withdrawalInfo = action.payload?.data ?? state.withdrawalInfo
                })
                .addCase(thunk.rejected, (state) => {
                    state.withdrawalSort.decision_loading = false
                })
        })
    },
})

export const { setWithdrawalSort, setEarningSort } = dataSlice.actions

export default dataSlice.reducer
